import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { App as AntdApp, ConfigProvider, theme } from 'antd';
import { buildAntdThemeConfig } from './antdTheme.js';
import { resolveThemeTokens } from './branding.js';
import { CHAT_UI_THEME_TOKENS, DEFAULT_CHAT_UI_THEME } from './themeTokens.js';

const ChatThemeContext = createContext(null);
const APPEARANCES = new Set(['system', 'light', 'dark']);
const NEXT_APPEARANCE = { light: 'dark', dark: 'light' };
const SYSTEM_APPEARANCE = { true: 'dark', false: 'light' };
const APPEARANCE_ALGORITHMS = { light: theme.defaultAlgorithm, dark: theme.darkAlgorithm };
export const useChatTheme = () => useContext(ChatThemeContext);

/** UI-owned AntD theme composition; does not own chat state or lifecycle. */
export const ChatThemeProvider = ({ children, themeTokens = null, branding = {}, appearance = 'system' }) => {
  const [selection, setSelection] = useState(null);
  const [systemAppearance, setSystemAppearance] = useState(DEFAULT_CHAT_UI_THEME);
  const requestedAppearance = APPEARANCES.has(appearance) ? appearance : 'system';
  const resolvedAppearance = { system: systemAppearance, light: 'light', dark: 'dark' }[selection ?? requestedAppearance];
  useEffect(() => {
    if (themeTokens || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setSystemAppearance(SYSTEM_APPEARANCE[Boolean(media.matches)]);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [themeTokens]);
  const resolvedTokens = useMemo(() => themeTokens ?? resolveThemeTokens({
    baseTokens: CHAT_UI_THEME_TOKENS[resolvedAppearance], branding, appearance: resolvedAppearance,
  }), [themeTokens, branding, resolvedAppearance]);
  const toggleAppearance = useCallback(() => setSelection((current) => NEXT_APPEARANCE[
    { system: systemAppearance, light: 'light', dark: 'dark' }[current ?? requestedAppearance]
  ]), [systemAppearance, requestedAppearance]);
  const value = useMemo(() => ({
    themeTokens: resolvedTokens, appearance: resolvedAppearance,
    toggleAppearance: themeTokens ? null : toggleAppearance,
  }), [resolvedTokens, resolvedAppearance, themeTokens, toggleAppearance]);
  const antdTheme = useMemo(() => {
    const config = buildAntdThemeConfig(resolvedTokens);
    if (themeTokens) return config;
    return { ...config, algorithm: (seed, mapToken) => ({
      ...APPEARANCE_ALGORITHMS[resolvedAppearance](seed, mapToken), ...config.token,
    }) };
  }, [resolvedTokens, themeTokens, resolvedAppearance]);
  return createElement(ChatThemeContext.Provider, { value }, createElement(ConfigProvider, { theme: antdTheme },
    createElement(AntdApp, {
      style: { height: 'var(--flare-app-viewport-height, 100dvh)', minHeight: 0,
        colorScheme: themeTokens ? undefined : resolvedAppearance },
    }, children)));
};
